import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useRecoilValue, useSetRecoilState } from 'recoil';

import { GiftChoiceUnitData } from '@shared/api/giftChoiceUnit/giftChoiceUnit.interface';
import { trackGiftEvent } from '@shared/api/giftEventLog/giftEventLog.controller';
import {
  giftItemForStoreDataAtomFamily,
  giftItemForStoreStatusAtomFamily,
} from '@shared/api/giftItem/giftItem.atom';
import { useGiftItemForStoreHook } from '@shared/api/giftItem/giftItem.hook';
import {
  AdditionalOption,
  GiftItemInfoType,
} from '@shared/api/giftItemInfo/giftItemInfo.interface';
import { GiftSelectedOption } from '@shared/api/giftOrder/giftOrder.interface';
import { Image } from '@shared/api/media/media.interface';
import { Loading } from '@shared/components/Loading';
import { fileBucketUrl } from '@shared/utils/fileUtils';
import { simpleOpenAtomFamily } from '@shared/state/atom';
import { formatPrice } from '@shared/utils/formatPrice';
import { giftDiscountRate } from '@shared/utils/giftDiscountRate.util';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import itemDetailImage from 'src/assets/gift/item-detail-image.png';

import {
  giftStoreNavBarScrolledAtom,
  giftStoreNavBarTitleAtom,
  giftStoreNavBarTransparentAtom,
} from '../containers/GiftStoreNavBar/giftStoreNavBar.atom';
import { GiftAdditionalOptions } from '../components/GiftAdditionalOptions';
import GiftDeliveryZoneGuideModal from '../components/GiftDeliveryZoneGuideModal';
import { GiftAdditionalOptionsDetail } from '../components/GiftAdditionalOptionsDetail';
import { GiftThanksPrintSection } from '../containers/GiftThanksPrintSection/GiftThanksPrintSection';
import { giftCartAtom, GiftCartItem } from '../state/giftCart.atom';
import {
  consumeGiftInputApplyRequest,
  giftInputApplyRequestAtom,
} from '../state/giftInputApply.atom';
import {
  giftItemOptionsStorageKey,
  removeLegacyGiftItemOptionsKey,
} from '../utils/giftItemOptionStorage';
import { buildSelectedOptions } from '../utils/giftOption.util';
import { resolveOrderThanksPrint } from '../utils/thanksPrintMessage';
import { resolveZoneFreeTiers } from '../utils/giftFreeShippingNudge.util';
import { getUnitSurcharge, resolveShippingFeeConfig } from '../utils/resolveDeliveryFee';

const ZONE_GUIDE_MODAL_ID = 'giftItemDetailZoneGuide';

const GiftItemDetailPage: FC = function GiftItemDetailPage() {
  const { itemId } = useParams<{ itemId?: string }>();
  const [searchParams] = useSearchParams();
  const bugoId = searchParams.get('bugoId');
  const navigate = useNavigate();

  const setTitle = useSetRecoilState(giftStoreNavBarTitleAtom);
  const setTransparent = useSetRecoilState(giftStoreNavBarTransparentAtom);
  const setScrolled = useSetRecoilState(giftStoreNavBarScrolledAtom);
  const setCartItems = useSetRecoilState(giftCartAtom);

  // 투명 모드 활성화 (마운트 시)
  useEffect(() => {
    setTitle(' ');
    setTransparent(true);
    setScrolled(false);

    return () => {
      setTransparent(false);
      setScrolled(false);
    };
  }, [setTitle, setTransparent, setScrolled]);

  useEffect(() => {
    if (bugoId && itemId) trackGiftEvent('item_view', bugoId, itemId);
  }, [bugoId, itemId]);
  // 상품 조회
  useGiftItemForStoreHook(itemId ?? '');
  const status = useRecoilValue(giftItemForStoreStatusAtomFamily(itemId ?? ''));
  const item = useRecoilValue(giftItemForStoreDataAtomFamily(itemId ?? ''));

  // Carousel 상태
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);

  // 수량 상태
  const [quantity, setQuantity] = useState(1);

  // 최소주문수량, 주문단위
  const minOrderQuantity = useMemo(() => {
    return item?.giftItemInfo?.minOrderQuantity || 1;
  }, [item]);

  const orderUnit = useMemo(() => {
    return item?.giftItemInfo?.orderUnit || 1;
  }, [item]);

  // 초기 수량 설정 — 소량주문 추가금(shippingFee)이 있는 상품은 면제기준 수량으로 시작
  // (기본이 6,990원처럼 추가금 없는 상태로 보이고, 줄이면 +추가금이 붙는 역방향)
  // 상품당 1회만 시딩 — 창 포커스 refetch로 item 참조가 바뀔 때마다 재실행되면
  // 사용자가 고른 수량(예: 70)이 초기값(예: 20)으로 조용히 리셋된다
  const quantitySeededItemIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (!item) return;
    if (quantitySeededItemIdRef.current === item._id) return;
    quantitySeededItemIdRef.current = item._id;
    const { shippingFee, freeShippingMinQuantity } = resolveShippingFeeConfig(
      item.giftItemInfo,
    );
    let initial = minOrderQuantity;
    if (
      shippingFee != null &&
      shippingFee > 0 &&
      freeShippingMinQuantity != null &&
      freeShippingMinQuantity >= minOrderQuantity
    ) {
      initial = freeShippingMinQuantity;
      if (orderUnit > 1) {
        const rem = (initial - minOrderQuantity) % orderUnit;
        if (rem !== 0) initial += orderUnit - rem;
      }
    }
    setQuantity(initial);
  }, [item, minOrderQuantity, orderUnit]);

  // 상품명 (오버라이드 우선)
  const productName = useMemo(() => {
    return item?.name || item?.giftItemInfo?.name || '';
  }, [item]);

  // 스크롤 감지 - 이미지 절반 지점에서 헤더 스타일 변경
  useEffect(() => {
    const handleScroll = () => {
      // 이미지는 aspect-square이므로 화면 너비 = 이미지 높이
      const imageHeight = window.innerWidth;
      const scrollThreshold = imageHeight / 2;
      const isScrolledPast = window.scrollY > scrollThreshold;

      setScrolled(isScrolledPast);

      // 스크롤 후에는 상품명을 타이틀로 표시
      if (isScrolledPast && productName) {
        setTitle(productName);
      } else {
        setTitle(' ');
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [setScrolled, setTitle, productName]);

  // 헤더 이미지 (오버라이드 우선 + choiceUnit 이미지 병합)
  const baseHeaderImages = useMemo(() => {
    if (item?.headerImages && item.headerImages.length > 0) {
      return item.headerImages as Image[];
    }
    if (item?.giftItemInfo?.headerImages && item.giftItemInfo.headerImages.length > 0) {
      return item.giftItemInfo.headerImages as Image[];
    }
    const mainImage = item?.image || item?.giftItemInfo?.image;
    if (mainImage && typeof mainImage === 'object') {
      return [mainImage as Image];
    }
    return [];
  }, [item]);

  // 상세 이미지 (오버라이드 우선)
  const detailImages = useMemo(() => {
    if (item?.detailImages && item.detailImages.length > 0) {
      return item.detailImages as Image[];
    }
    if (item?.giftItemInfo?.detailImages && item.giftItemInfo.detailImages.length > 0) {
      return item.giftItemInfo.detailImages as Image[];
    }
    return [];
  }, [item]);

  // 카테고리 상세(맥락설명) 이미지 — 상품 상세 이미지보다 먼저 표시
  const categoryDetailImages = useMemo(() => {
    const category = item?.giftItemInfo?.giftDeliveryCategory;
    if (
      category &&
      typeof category === 'object' &&
      Array.isArray(category.detailImages) &&
      category.detailImages.length > 0
    ) {
      return category.detailImages as Image[];
    }
    return [];
  }, [item]);

  // 상품 타입
  const productType = useMemo(() => {
    return item?.giftItemInfo?.type || GiftItemInfoType.Delivery;
  }, [item]);

  // 쿠폰 상세
  const couponDetail = useMemo(() => {
    return item?.giftItemInfo?.couponDetail;
  }, [item]);

  // 배송 상세 (원상품 오버라이드 우선, 없으면 브랜드 기본값)
  const deliveryDetail = useMemo(() => {
    const brand = item?.giftItemInfo?.giftItemBrand;
    const brandDetail =
      brand && typeof brand === 'object' && 'deliveryDetail' in brand
        ? brand.deliveryDetail
        : undefined;
    const itemDetail = item?.giftItemInfo?.deliveryDetail;

    const shippingInfo = itemDetail?.shippingInfo || brandDetail?.shippingInfo;
    const shippingFeeInfo = itemDetail?.shippingFeeInfo || brandDetail?.shippingFeeInfo;

    if (!shippingInfo && !shippingFeeInfo) return null;
    return { shippingInfo, shippingFeeInfo };
  }, [item]);

  // 권역(읍면동) 배송비 브랜드 — 배송 가능 지역·배송비 안내 시트를 연다 (떡보의하루 등)
  const zoneBrandId = useMemo(() => {
    const brand = item?.giftItemInfo?.giftItemBrand;
    if (!brand || typeof brand !== 'object') return undefined;
    return brand.deliveryDetail?.zonePolicy?.enabled ? brand._id : undefined;
  }, [item]);
  const setZoneGuideOpen = useSetRecoilState(simpleOpenAtomFamily(ZONE_GUIDE_MODAL_ID));

  // 브랜드 이름
  const brandName = useMemo(() => {
    const brand = item?.giftItemInfo?.giftItemBrand;
    if (brand && typeof brand === 'object' && 'name' in brand) {
      return brand.name;
    }
    return null;
  }, [item]);

  // 감사메시지 인쇄 브랜드 정보 — 상품 오버라이드(useThanksPrint)·허용 템플릿 제한까지
  // 결제 화면과 동일한 공용 리졸버로 판정
  const thanksPrintInfo = useMemo(
    () =>
      resolveOrderThanksPrint(
        item ? [{ itemId: item._id, giftItemInfo: item.giftItemInfo }] : [],
      ),
    [item],
  );

  // 선택형 상품 여부 및 선택 옵션
  const isSelectable = useMemo(() => {
    return item?.giftItemInfo?.isSelectable || false;
  }, [item]);

  const choiceUnits = useMemo(() => {
    const units = item?.giftItemInfo?.choiceUnits;
    if (!units || !Array.isArray(units)) return [];
    return units.filter(
      (unit): unit is GiftChoiceUnitData => typeof unit === 'object' && unit !== null,
    );
  }, [item]);

  // choiceUnits에서 이미지가 있는 항목만 추출
  const choiceUnitImages = useMemo(() => {
    if (!isSelectable || choiceUnits.length === 0) return [];
    return choiceUnits
      .filter((unit) => unit.image && typeof unit.image === 'object')
      .map((unit) => unit.image as Image);
  }, [isSelectable, choiceUnits]);

  // headerImages + choiceUnit 이미지 병합
  const headerImages = useMemo(() => {
    if (choiceUnitImages.length === 0) return baseHeaderImages;
    const existingUrls = new Set(baseHeaderImages.map((img) => img.url));
    const uniqueChoiceImages = choiceUnitImages.filter(
      (img) => !existingUrls.has(img.url),
    );
    return [...baseHeaderImages, ...uniqueChoiceImages];
  }, [baseHeaderImages, choiceUnitImages]);

  const choiceUnitImageStartIndex = baseHeaderImages.length;

  // 스와이프 감지 (최소 50px 이동)
  const minSwipeDistance = 50;

  const onTouchStart = useCallback((e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0]?.clientX ?? null);
  }, []);

  const onTouchMove = useCallback((e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0]?.clientX ?? null);
  }, []);

  const onTouchEnd = useCallback(() => {
    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe) {
      setCurrentImageIndex((prev) => (prev === headerImages.length - 1 ? 0 : prev + 1));
    } else if (isRightSwipe) {
      setCurrentImageIndex((prev) => (prev === 0 ? headerImages.length - 1 : prev - 1));
    }
  }, [touchStart, touchEnd, headerImages.length]);

  // 상품 설명 (오버라이드 우선)
  const detailInfo = useMemo(() => {
    return item?.detailInfo || item?.giftItemInfo?.detailInfo || '';
  }, [item]);

  // 가격
  const priceRegular = item?.priceRegular || 0;
  const priceRetail = item?.priceRetail || 0;
  const discountRate = giftDiscountRate(priceRegular, priceRetail);

  // 추가옵션
  const additionalOptions = useMemo<AdditionalOption<Image>[]>(() => {
    const opts = item?.giftItemInfo?.additionalOptions;
    return Array.isArray(opts) ? (opts as AdditionalOption<Image>[]) : [];
  }, [item]);

  // 옵션 선택 영역 / 옵션 안내 영역 (스크롤 타겟)
  const optionsRef = useRef<HTMLDivElement>(null);
  const optionsDetailRef = useRef<HTMLDivElement>(null);

  // 필수 미선택 시 강조할 옵션명
  const [highlightedOption, setHighlightedOption] = useState<string | null>(null);

  // 안내(이미지/설명)가 있는 옵션명 집합 — '옵션 보러가기' 노출 여부
  const optionsWithInfo = useMemo(() => {
    const names = new Set<string>();
    for (const opt of additionalOptions) {
      const hasInfo = (opt.choices ?? []).some(
        (c) =>
          c.enabled !== false &&
          ((c.descriptionImage && typeof c.descriptionImage === 'object') ||
            c.description),
      );
      if (hasInfo) names.add(opt.name);
    }
    return names;
  }, [additionalOptions]);

  // optionName -> 슬롯별 선택한 choiceName (index = slotIndex)
  // 부고(상주) + 상품별로 localStorage에 저장 → 페이지를 오가도 선택이 유지되면서,
  // 다른 상주 응대 때 앞사람의 옵션 선택이 딸려오지 않는다 (utils/giftItemOptionStorage)
  const optionsStorageKey = giftItemOptionsStorageKey(bugoId, itemId);
  const loadStoredChoices = useCallback((key: string): Record<string, string[]> => {
    if (!key) return {};
    try {
      const raw = localStorage.getItem(key);
      const parsed = raw ? JSON.parse(raw) : null;
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
      return {};
    }
  }, []);

  const [selectedChoices, setSelectedChoices] = useState<Record<string, string[]>>(() =>
    loadStoredChoices(optionsStorageKey),
  );
  // 현재 selectedChoices가 어느 상품(키) 것인지 추적 — 상품 전환 시 교차 저장 방지
  const loadedOptionsKeyRef = useRef(optionsStorageKey);

  // 선택이 바뀌면 저장 (현재 키에 해당하는 선택일 때만)
  useEffect(() => {
    if (!optionsStorageKey || optionsStorageKey !== loadedOptionsKeyRef.current) return;
    try {
      if (Object.keys(selectedChoices).length > 0) {
        localStorage.setItem(optionsStorageKey, JSON.stringify(selectedChoices));
      } else {
        localStorage.removeItem(optionsStorageKey);
      }
    } catch {
      /* 저장 실패는 무시 */
    }
  }, [optionsStorageKey, selectedChoices]);

  // 다른 상품(또는 다른 상주)으로 전환되면 그 키의 저장값으로 다시 로딩
  useEffect(() => {
    if (optionsStorageKey === loadedOptionsKeyRef.current) return;
    loadedOptionsKeyRef.current = optionsStorageKey;
    setSelectedChoices(loadStoredChoices(optionsStorageKey));
  }, [optionsStorageKey, loadStoredChoices]);

  // 부고 구분 없이 저장했던 구버전 키 정리 (남의 옵션이 계속 떠다니는 것 방지)
  useEffect(() => {
    removeLegacyGiftItemOptionsKey(itemId);
  }, [itemId]);

  // 대리주문 패널의 "주문서 정리" — 이 상품의 옵션 선택도 함께 비운다
  const applyRequest = useRecoilValue(giftInputApplyRequestAtom);
  useEffect(() => {
    if (!applyRequest?.resetToMemberDefault || !bugoId) return;
    if (applyRequest.bugoId !== bugoId) return;
    if (!consumeGiftInputApplyRequest('options-reset', applyRequest.requestedAt)) return;
    setSelectedChoices({});
  }, [applyRequest, bugoId]);

  const handleSelectOption = useCallback(
    (optionName: string, slotIndex: number, choiceName: string) => {
      setSelectedChoices((prev) => {
        const arr = [...(prev[optionName] ?? [])];
        arr[slotIndex] = choiceName;
        return { ...prev, [optionName]: arr };
      });
    },
    [],
  );

  // 선택값 스냅샷 (주문 전송용) — selectionCount만큼 슬롯별 entry, count>1이면 slotIndex 포함
  const selectedOptions = useMemo<GiftSelectedOption[]>(
    () => buildSelectedOptions(additionalOptions, selectedChoices),
    [additionalOptions, selectedChoices],
  );

  // 옵션 추가금 합계 + 단가
  const optionDelta = useMemo(
    () => selectedOptions.reduce((sum, o) => sum + o.priceDelta, 0),
    [selectedOptions],
  );

  // 개당 소량주문 추가금 (수량이 면제 기준 이상이면 0). 상품값 → 브랜드값 오버라이드
  const unitSurcharge = useMemo(
    () => getUnitSurcharge(item?.giftItemInfo, quantity),
    [item, quantity],
  );

  // 추가금 면제까지 더 담으면 되는 수량 — 구매 유도용
  const surchargeNudge = useMemo(() => {
    const { shippingFee, freeShippingMinQuantity } = resolveShippingFeeConfig(
      item?.giftItemInfo,
    );
    if (!shippingFee || shippingFee <= 0 || freeShippingMinQuantity == null) return null;
    if (quantity >= freeShippingMinQuantity) {
      return {
        reached: true,
        minQty: freeShippingMinQuantity,
        unitSurcharge: shippingFee,
      };
    }
    return {
      reached: false,
      remain: freeShippingMinQuantity - quantity,
      minQty: freeShippingMinQuantity,
      unitSurcharge: shippingFee,
    };
  }, [item, quantity]);

  const unitPrice = priceRetail + optionDelta + unitSurcharge;

  // 하단 고정 바 높이 실측 → 본문 하단 여백 (칩 줄 수·글자 확대에 따라 바 높이가 변해 고정 여백으론 본문이 가려진다)
  const bottomBarRef = useRef<HTMLDivElement>(null);
  const [bottomBarHeight, setBottomBarHeight] = useState(0);
  useEffect(() => {
    const el = bottomBarRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(() => setBottomBarHeight(el.offsetHeight));
    observer.observe(el);
    setBottomBarHeight(el.offsetHeight);
    return () => observer.disconnect();
  }, [item]);

  // 권역 배송비 브랜드 — 배송 정보 박스에 무료 기준 금액만 안내 (몇 개 더 담기 안내는 주문서에서)
  const zoneFreeTiers = useMemo(() => {
    const brand = item?.giftItemInfo?.giftItemBrand;
    if (!brand || typeof brand !== 'object') return [];
    return resolveZoneFreeTiers(brand.deliveryDetail?.zonePolicy);
  }, [item]);

  // 추가금 면제(=더 저렴) 기준 수량 — 빠른수량 칩 강조용
  const freeThreshold = useMemo(
    () => resolveShippingFeeConfig(item?.giftItemInfo).freeShippingMinQuantity,
    [item],
  );

  // 빠른 수량 프리셋 (최소주문수량 이상, 면제기준 포함, 주문단위 정렬)
  const quantityPresets = useMemo(() => {
    const set = new Set<number>([10, 20, 30, 50, 100]);
    if (freeThreshold != null) set.add(freeThreshold);
    return Array.from(set)
      .map((n) => {
        let v = Math.max(n, minOrderQuantity);
        if (orderUnit > 1) {
          const rem = (v - minOrderQuantity) % orderUnit;
          if (rem !== 0) v += orderUnit - rem;
        }
        return v;
      })
      .filter((v, i, arr) => arr.indexOf(v) === i)
      .sort((a, b) => a - b)
      .slice(0, 6);
  }, [freeThreshold, minOrderQuantity, orderUnit]);

  // 필수 옵션 미선택 검사 → 미선택 슬롯 라벨 목록 (slot별로 검사)
  const missingRequired = useMemo(() => {
    const result: string[] = [];
    for (const opt of additionalOptions) {
      if (!opt.required) continue;
      const count = Math.max(1, opt.selectionCount ?? 1);
      const slots = selectedChoices[opt.name] ?? [];
      for (let s = 0; s < count; s++) {
        if (!slots[s]) {
          result.push(
            count > 1 ? opt.slotLabels?.[s]?.trim() || `${opt.name}${s + 1}` : opt.name,
          );
        }
      }
    }
    return result;
  }, [additionalOptions, selectedChoices]);

  // 첫 번째 미선택 필수 옵션명 (스크롤 타겟)
  const firstMissingOptionName = useMemo(() => {
    for (const opt of additionalOptions) {
      if (!opt.required) continue;
      const count = Math.max(1, opt.selectionCount ?? 1);
      const slots = selectedChoices[opt.name] ?? [];
      for (let s = 0; s < count; s++) {
        if (!slots[s]) return opt.name;
      }
    }
    return null;
  }, [additionalOptions, selectedChoices]);

  // 강조는 잠시 후 해제
  useEffect(() => {
    if (!highlightedOption) return;
    const t = setTimeout(() => setHighlightedOption(null), 2500);
    return () => clearTimeout(t);
  }, [highlightedOption]);

  // 옵션 안내 영역의 해당 옵션으로 스크롤 ('옵션 보러가기')
  const handleViewOptionInfo = useCallback((optionName: string) => {
    const el = optionsDetailRef.current?.querySelector<HTMLElement>(
      `[data-gift-option-info="${CSS.escape(optionName)}"]`,
    );
    el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  // 옵션 선택 영역의 해당 옵션으로 스크롤 ('옵션 선택하러 가기')
  const handleGoToOptionSelect = useCallback((optionName: string) => {
    const el = optionsRef.current?.querySelector<HTMLElement>(
      `[data-gift-option="${CSS.escape(optionName)}"]`,
    );
    el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setHighlightedOption(optionName);
  }, []);

  // 수량 증가
  const increaseQuantity = useCallback(() => {
    setQuantity((prev) => prev + orderUnit);
  }, [orderUnit]);

  // 수량 감소
  const decreaseQuantity = useCallback(() => {
    setQuantity((prev) => {
      const newQty = prev - orderUnit;
      return newQty >= minOrderQuantity ? newQty : prev;
    });
  }, [orderUnit, minOrderQuantity]);

  // 수량 직접 입력
  const handleQuantityChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value, 10);
    if (!isNaN(value) && value >= 0) {
      setQuantity(value);
    }
  }, []);

  // 입력 완료 시 최소수량/주문단위 조정
  const handleQuantityBlur = useCallback(() => {
    setQuantity((prev) => {
      // 최소주문수량 이하면 최소주문수량으로
      if (prev < minOrderQuantity) {
        return minOrderQuantity;
      }
      // 주문단위에 맞게 조정 (올림)
      if (orderUnit > 1) {
        const remainder = (prev - minOrderQuantity) % orderUnit;
        if (remainder !== 0) {
          return prev + (orderUnit - remainder);
        }
      }
      return prev;
    });
  }, [minOrderQuantity, orderUnit]);

  // 대표 이미지 URL
  const mainImageUrl = useMemo(() => {
    const img = item?.image || item?.giftItemInfo?.image;
    if (img && typeof img === 'object' && 'url' in img) {
      return (img as Image).url;
    }
    return '';
  }, [item]);

  // 필수 옵션 미선택 시 경고 + 해당 옵션으로 스크롤/강조 (통과하면 true)
  const ensureRequiredSelected = useCallback(() => {
    if (missingRequired.length > 0) {
      alert(`필수 옵션을 선택해주세요: ${missingRequired.join(', ')}`);
      if (firstMissingOptionName) {
        const el = optionsRef.current?.querySelector<HTMLElement>(
          `[data-gift-option="${CSS.escape(firstMissingOptionName)}"]`,
        );
        el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setHighlightedOption(firstMissingOptionName);
      }
      return false;
    }
    return true;
  }, [missingRequired, firstMissingOptionName]);

  // 같은 상품+같은 옵션이면 한 라인으로 합침 (슬롯 번호까지 포함해 구분)
  const optionSignature = (opts?: GiftSelectedOption[]) =>
    (opts ?? [])
      .map((o) => `${o.optionName}#${o.slotIndex ?? 0}:${o.choiceName}`)
      .join('|');

  // 장바구니 담기
  const handleAddToCart = useCallback(() => {
    if (!itemId || !bugoId) return;
    if (!ensureRequiredSelected()) return;

    const sig = optionSignature(selectedOptions);

    setCartItems((prev) => {
      // 같은 상품 + 같은 옵션 조합이 있는지 확인
      const existingItem = prev.find(
        (cartItem) =>
          cartItem.itemId === itemId && optionSignature(cartItem.selectedOptions) === sig,
      );

      if (existingItem) {
        return prev.map((cartItem) =>
          cartItem.itemId === itemId && optionSignature(cartItem.selectedOptions) === sig
            ? { ...cartItem, quantity }
            : cartItem,
        );
      } else {
        const newCartItem: GiftCartItem = {
          itemId: itemId,
          itemName: productName,
          itemImage: mainImageUrl,
          priceRetail,
          quantity,
          minOrderQuantity,
          orderUnit,
          bugoId: bugoId,
          ...(selectedOptions.length > 0 && { selectedOptions }),
        };
        return [...prev, newCartItem];
      }
    });

    alert(`'${productName}' ${quantity}개가 장바구니에 담겼습니다.`);
  }, [
    itemId,
    bugoId,
    ensureRequiredSelected,
    selectedOptions,
    productName,
    mainImageUrl,
    priceRetail,
    quantity,
    minOrderQuantity,
    orderUnit,
    setCartItems,
  ]);

  // 바로 구매 (선택옵션은 nav state로 전달)
  const handleBuyNow = useCallback(() => {
    if (!ensureRequiredSelected()) return;
    navigate(
      `/gift-store/item/${itemId}/checkout?bugoId=${bugoId}&quantity=${quantity}`,
      { state: { selectedOptions } },
    );
  }, [navigate, itemId, bugoId, quantity, selectedOptions, ensureRequiredSelected]);

  // 총 금액 (옵션 추가금 포함)
  const totalPrice = unitPrice * quantity;

  if (status !== 'success' || !item) {
    return (
      <div className="center-box min-h-screen">
        <Loading />
      </div>
    );
  }

  return (
    <div
      className="min-h-screen bg-white pb-26"
      style={bottomBarHeight > 0 ? { paddingBottom: bottomBarHeight + 8 } : undefined}
    >
      {/* 헤더 이미지 Carousel */}
      {headerImages.length > 0 && (
        <div className="relative">
          <div
            className="aspect-square w-full overflow-hidden"
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
          >
            <div
              className="flex h-full transition-transform duration-300 ease-out"
              style={{ transform: `translateX(-${currentImageIndex * 100}%)` }}
            >
              {headerImages.map((img, idx) => {
                const isChoiceUnitImg = idx >= choiceUnitImageStartIndex;
                const choiceUnit = isChoiceUnitImg
                  ? choiceUnits.find(
                      (u) =>
                        u.image &&
                        typeof u.image === 'object' &&
                        (u.image as Image).url === img.url,
                    )
                  : null;

                return (
                  <div key={idx} className="relative h-full w-full flex-shrink-0">
                    <img
                      src={fileBucketUrl(img?.url ?? '', 'raw')}
                      alt={`${productName} ${idx + 1}`}
                      className="h-full w-full object-cover"
                    />
                    {isChoiceUnitImg && choiceUnit && (
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent px-4 pb-4 pt-10">
                        <span className="inline-block rounded-full bg-indigo-500 px-2.5 py-0.5 text-xs font-bold text-white">
                          선택옵션
                        </span>
                        <p className="mt-1 text-base font-bold text-white">
                          {choiceUnit.name}
                        </p>
                        {choiceUnit.priceRegular && (
                          <p className="text-sm text-white/80">
                            정가 {formatPrice(choiceUnit.priceRegular)}원
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Carousel 네비게이션 */}
          {headerImages.length > 1 && (
            <>
              {/* 좌우 버튼 */}
              <button
                onClick={() =>
                  setCurrentImageIndex((prev) =>
                    prev === 0 ? headerImages.length - 1 : prev - 1,
                  )
                }
                className="absolute left-2 top-1/2 -translate-y-1/2 rounded-full bg-black/30 p-2 text-white"
              >
                ◀
              </button>
              <button
                onClick={() =>
                  setCurrentImageIndex((prev) =>
                    prev === headerImages.length - 1 ? 0 : prev + 1,
                  )
                }
                className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full bg-black/30 p-2 text-white"
              >
                ▶
              </button>

              {/* 인디케이터 */}
              <div className="absolute bottom-3 right-3 rounded-full bg-black/50 px-2.5 py-1 text-xs font-medium text-white">
                {currentImageIndex + 1} / {headerImages.length}
              </div>
            </>
          )}
        </div>
      )}

      {/* 상품 정보 */}
      <div className="p-4">
        {/* 브랜드 & 타입 뱃지 */}
        <div className="flex flex-wrap items-center gap-1.5">
          {isSelectable && (
            <span className="inline-block rounded-full bg-gradient-to-r from-indigo-500 to-purple-500 px-2.5 py-0.5 text-xs font-bold text-white">
              선택형
            </span>
          )}
          {brandName && (
            <span className="inline-block rounded bg-orange-500 px-2 py-0.5 text-xs font-bold text-white">
              {brandName}
            </span>
          )}
          {productType === GiftItemInfoType.Delivery && (
            <span className="inline-block rounded bg-blue-500 px-2 py-0.5 text-xs font-bold text-white">
              배송형
            </span>
          )}
          {productType === GiftItemInfoType.Coupon && (
            <span className="inline-block rounded bg-purple-500 px-2 py-0.5 text-xs font-bold text-white">
              쿠폰형
            </span>
          )}
        </div>

        {/* 상품명 */}
        <h1 className="mt-2 text-xl font-bold">{productName}</h1>

        {/* 가격 */}
        <div className="mt-3 flex items-end gap-2">
          {discountRate > 0 && (
            <>
              <span className="text-2xl font-bold text-red-500">{discountRate}%</span>
              <span className="text-lg text-gray-400 line-through">
                {formatPrice(priceRegular)}원
              </span>
            </>
          )}
          <span className="text-2xl font-bold">{formatPrice(priceRetail)}원</span>
        </div>

        {/* 추가옵션 선택 */}
        <div ref={optionsRef}>
          <GiftAdditionalOptions
            options={additionalOptions}
            selected={selectedChoices}
            onSelect={handleSelectOption}
            highlightedName={highlightedOption}
            hasInfo={(name) => optionsWithInfo.has(name)}
            onViewInfo={handleViewOptionInfo}
          />
        </div>

        {/* 소량주문 추가금 — 옵션처럼 단가에 더해지는 항목으로 표시 (수량 줄여 면제기준 미만일 때) */}
        {unitSurcharge > 0 && surchargeNudge && (
          <div className="mt-2 flex items-center justify-between rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm">
            <span className="text-amber-800">
              소량주문 ({surchargeNudge.minQty}개 미만)
            </span>
            <span className="font-bold text-amber-700">
              +{formatPrice(unitSurcharge)}원/개
            </span>
          </div>
        )}

        {/* 옵션/추가금 합산 단가 안내 */}
        {(optionDelta > 0 || unitSurcharge > 0) && (
          <p className="mt-2 text-sm text-gray-500">
            {optionDelta > 0 && (
              <>
                옵션 추가금{' '}
                <span className="font-semibold text-gray-700">
                  +{formatPrice(optionDelta)}원
                </span>{' '}
              </>
            )}
            → 단가{' '}
            <span className="font-semibold text-gray-900">
              {formatPrice(unitPrice)}원
            </span>
          </p>
        )}

        {/* 선택형 상품 안내 배너 */}
        {isSelectable && choiceUnits.length > 0 && (
          <div className="mt-4 overflow-hidden rounded-xl border border-indigo-200 bg-gradient-to-r from-indigo-50 to-purple-50">
            <div className="flex items-start gap-3 p-4">
              <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-indigo-100 text-lg">
                🎁
              </div>
              <div>
                <p className="text-sm font-bold text-indigo-800">
                  {choiceUnits.length}가지 옵션 중 선택 가능!
                </p>
                <p className="mt-0.5 text-xs text-indigo-600/80">
                  받으시는 분이 원하는 상품을 직접 선택할 수 있어요
                </p>
              </div>
            </div>
          </div>
        )}

        {/* 쿠폰 정보 (쿠폰형일 때) */}
        {productType === GiftItemInfoType.Coupon && couponDetail && (
          <div className="mt-4 rounded-lg bg-purple-50 p-4">
            <h3 className="font-bold text-purple-700">쿠폰 정보</h3>
            <div className="mt-2 space-y-1 text-sm">
              {couponDetail.validPeriod && (
                <div className="flex">
                  <span className="w-16 text-gray-500">유효기간</span>
                  <span className="font-medium">{couponDetail.validPeriod}</span>
                </div>
              )}
              {couponDetail.placeOfExchange && (
                <div className="flex">
                  <span className="w-16 text-gray-500">사용처</span>
                  <span className="font-medium">{couponDetail.placeOfExchange}</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* 배송 정보 (배송형일 때) */}
        {productType === GiftItemInfoType.Delivery && (deliveryDetail || zoneBrandId) && (
          <div className="mt-4 rounded-lg bg-blue-50 p-4">
            <div className="flex items-center justify-between gap-2">
              <h3 className="font-bold text-blue-700">배송 정보</h3>
              {zoneBrandId && (
                <button
                  type="button"
                  onClick={() => setZoneGuideOpen(true)}
                  className="flex-shrink-0 rounded-full border bg-white px-2.5 py-1 text-xs font-bold theme-boder-main theme-text-main"
                >
                  배송 가능 지역·배송비 확인
                </button>
              )}
            </div>
            {zoneBrandId && (
              <p className="mt-1 break-keep text-xs theme-text-main">
                배송지 읍·면·동에 따라 배송비가 다르고, 배송이 불가능한 지역이 있습니다.
                주문서에서 주소를 입력하면 배송비가 자동 계산됩니다.
              </p>
            )}
            {zoneFreeTiers.length > 0 && (
              <p className="mt-1 break-keep text-xs font-bold theme-text-main">
                {zoneFreeTiers
                  .map((tier) =>
                    tier.label
                      ? `${tier.label}은 ${formatPrice(tier.freeOverAmount)}원 이상`
                      : `${formatPrice(tier.freeOverAmount)}원 이상`,
                  )
                  .join(' · ')}{' '}
                주문 시 기본배송비 무료
              </p>
            )}
            <div className="mt-2 space-y-1 text-sm">
              {deliveryDetail?.shippingInfo && (
                <div className="flex">
                  <span className="w-16 flex-shrink-0 text-gray-500">배송안내</span>
                  <span className="whitespace-pre-wrap break-keep font-medium">
                    {deliveryDetail.shippingInfo}
                  </span>
                </div>
              )}
              {deliveryDetail?.shippingFeeInfo && (
                <div className="flex">
                  <span className="w-16 flex-shrink-0 text-gray-500">배송비</span>
                  <span className="whitespace-pre-wrap font-medium">
                    {deliveryDetail.shippingFeeInfo}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* 선택 가능 옵션 (선택형일 때) */}
        {isSelectable && choiceUnits.length > 0 && (
          <div className="mt-4 overflow-hidden rounded-xl border border-indigo-100">
            <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-4 py-3">
              <h3 className="text-sm font-bold text-white">
                선택 가능한 옵션 ({choiceUnits.length}종)
              </h3>
              <p className="mt-0.5 text-xs text-white/80">
                받으시는 분이 아래 중 하나를 선택합니다
              </p>
            </div>
            <div className="bg-gradient-to-br from-indigo-50/50 to-purple-50/50 p-3">
              <div className="flex gap-2 overflow-x-auto pb-1">
                {choiceUnits.map((unit, unitIdx) => {
                  const unitBrandName =
                    unit.giftItemBrand &&
                    typeof unit.giftItemBrand === 'object' &&
                    'name' in unit.giftItemBrand
                      ? unit.giftItemBrand.name
                      : null;

                  return (
                    <div
                      key={unit._id}
                      className="flex w-28 flex-shrink-0 flex-col overflow-hidden rounded-xl bg-white shadow-sm ring-1 ring-gray-100"
                    >
                      <div className="relative aspect-square w-full overflow-hidden bg-gray-100">
                        {unit.image ? (
                          <img
                            src={fileBucketUrl(
                              typeof unit.image === 'object'
                                ? unit.image.url
                                : unit.image,
                              'w200',
                            )}
                            alt={unit.name}
                            className="h-full w-full object-cover"
                          />
                        ) : (
                          <div className="flex h-full w-full items-center justify-center text-xs text-gray-400">
                            No Image
                          </div>
                        )}
                        <span className="absolute left-1 top-1 rounded bg-black/60 px-1.5 py-0.5 text-[10px] font-bold text-white">
                          {unitIdx + 1}
                        </span>
                      </div>
                      <div className="flex flex-1 flex-col p-2">
                        {unitBrandName && (
                          <span className="mb-1 self-start rounded bg-orange-100 px-1.5 py-0.5 text-[10px] font-bold text-orange-700">
                            {unitBrandName}
                          </span>
                        )}
                        <span className="line-clamp-2 text-xs font-medium text-gray-800">
                          {unit.name}
                        </span>
                        {unit.priceRegular && (
                          <span className="mt-auto pt-1 text-xs font-bold text-indigo-600">
                            {formatPrice(unit.priceRegular)}원
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* 상품 설명 */}
        {detailInfo && (
          <div className="mt-6">
            <h3 className="font-bold text-gray-700">상품 설명</h3>
            <p className="mt-2 whitespace-pre-wrap break-keep text-sm text-gray-600">
              {detailInfo}
            </p>
          </div>
        )}

        {/* 주문 정보 */}
        <div className="mt-6 rounded-lg bg-gray-50 p-3 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-500">최소 주문수량</span>
            <span className="font-medium">{minOrderQuantity}개</span>
          </div>
          {orderUnit > 1 && (
            <div className="mt-1 flex justify-between">
              <span className="text-gray-500">주문 단위</span>
              <span className="font-medium">{orderUnit}개씩</span>
            </div>
          )}
        </div>
      </div>

      {/* 선택형 상품 설명 이미지 (쿠폰형 + 선택형일 때) */}
      {productType === GiftItemInfoType.Coupon && isSelectable && (
        <div className="border-t">
          <img src={itemDetailImage} alt="선택형 답례품 안내" className="w-full" />
        </div>
      )}

      {/* 선택 옵션별 상세 이미지 */}
      {isSelectable && choiceUnits.length > 0 && (
        <div className="border-t">
          <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-4 py-3">
            <h3 className="text-base font-bold text-white">
              선택 가능한 옵션 상세 ({choiceUnits.length}종)
            </h3>
          </div>
          {choiceUnits.map((unit) => {
            const unitBrandName =
              unit.giftItemBrand &&
              typeof unit.giftItemBrand === 'object' &&
              'name' in unit.giftItemBrand
                ? unit.giftItemBrand.name
                : null;

            return (
              <div key={unit._id} className="border-b border-gray-100 last:border-b-0">
                <div className="flex items-center gap-3 bg-gray-50 px-4 py-3">
                  {unit.image && typeof unit.image === 'object' && (
                    <img
                      src={fileBucketUrl((unit.image as Image).url, 'w200')}
                      alt={unit.name}
                      className="h-12 w-12 rounded-lg object-cover"
                    />
                  )}
                  <div className="flex-1">
                    <div className="flex items-center gap-1.5">
                      {unitBrandName && (
                        <span className="break-keep rounded bg-orange-100 px-1.5 py-0.5 text-[10px] font-bold text-orange-700">
                          {unitBrandName}
                        </span>
                      )}
                      <span className="break-keep text-sm font-bold text-gray-800">
                        {unit.name}
                      </span>
                    </div>
                    {unit.priceRegular && (
                      <span className="text-xs text-gray-500">
                        정가 {formatPrice(unit.priceRegular)}원
                      </span>
                    )}
                  </div>
                </div>
                {unit.description && (
                  <div className="bg-white px-4 py-3">
                    <p className="whitespace-pre-wrap break-keep text-sm leading-relaxed text-gray-600">
                      {unit.description}
                    </p>
                  </div>
                )}
                {unit.image && typeof unit.image === 'object' && (
                  <img
                    src={fileBucketUrl((unit.image as Image).url, 'raw')}
                    alt={`${unit.name} 상세`}
                    className="w-full"
                  />
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* 감사메시지 인쇄 (해당 브랜드 상품일 때, 상세 이미지 앞) */}
      {thanksPrintInfo && (
        <GiftThanksPrintSection
          thanksPrint={thanksPrintInfo.config}
          brandId={thanksPrintInfo.brandId}
          bugoId={bugoId}
          allowedTemplateIds={thanksPrintInfo.allowedTemplateIds}
        />
      )}

      {/* 옵션 안내 (상세 이미지 직전) */}
      <div ref={optionsDetailRef}>
        <GiftAdditionalOptionsDetail
          options={additionalOptions}
          onSelectOption={handleGoToOptionSelect}
        />
      </div>

      {/* 카테고리 상세(맥락설명) 이미지 — 상품 상세 이미지보다 먼저 표시 */}
      {categoryDetailImages.length > 0 && (
        <div className="mt-4 border-t pt-4">
          <div className="mt-2">
            {categoryDetailImages.map((img, idx) => (
              <img
                key={`category-detail-${idx}`}
                src={fileBucketUrl(img.url, 'raw')}
                alt={`카테고리 상세이미지 ${idx + 1}`}
                className="w-full"
              />
            ))}
          </div>
        </div>
      )}

      {/* 상세 이미지 */}
      {detailImages.length > 0 && (
        <div className="mt-4 border-t pt-4">
          <h3 className="px-4 font-bold text-gray-700">상세 정보</h3>
          <div className="mt-2">
            {detailImages.map((img, idx) => (
              <img
                key={`detail-${idx}`}
                src={fileBucketUrl(img.url, 'raw')}
                alt={`상세이미지 ${idx + 1}`}
                className="w-full"
              />
            ))}
          </div>
        </div>
      )}

      {/* 하단 고정 영역 */}
      <div
        ref={bottomBarRef}
        className="fixed bottom-0 left-0 right-0 border-t bg-white px-3 py-2 shadow-lg"
      >
        <div className="mx-auto max-w-lg space-y-2">
          {/* 빠른 수량 칩 */}
          {quantityPresets.length > 0 && (
            <div className="flex items-center gap-1.5 overflow-x-auto">
              <span className="shrink-0 text-[11px] text-gray-400">빠른수량</span>
              {quantityPresets.map((p) => {
                const active = quantity === p;
                return (
                  <button
                    key={p}
                    type="button"
                    onClick={() => setQuantity(p)}
                    className={`shrink-0 rounded-full border px-3 py-0.5 text-xs font-bold transition-colors ${
                      active
                        ? 'border-red-400 bg-red-50 text-red-600'
                        : 'border-gray-300 bg-white text-gray-600'
                    }`}
                  >
                    {p}개
                  </button>
                );
              })}
            </div>
          )}

          {/* 수량 조절 (한 줄) */}
          <div className="relative flex items-center justify-center">
            <span className="absolute left-2 text-sm text-gray-600">수량</span>
            <div className="flex items-center">
              <button
                onClick={decreaseQuantity}
                disabled={quantity <= minOrderQuantity}
                className="flex h-8 w-12 items-center justify-center rounded-lg border-2 border-gray-300 bg-gray-100 text-xl font-bold text-gray-700 active:bg-gray-300 disabled:border-gray-200 disabled:bg-gray-50 disabled:text-gray-300"
              >
                −
              </button>
              <input
                type="number"
                inputMode="numeric"
                value={quantity}
                onChange={handleQuantityChange}
                onBlur={handleQuantityBlur}
                onFocus={(e) => e.target.select()}
                className="w-[64px] bg-transparent text-center text-base font-bold outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
              />
              <button
                onClick={increaseQuantity}
                className="flex h-8 w-12 items-center justify-center rounded-lg border-2 border-gray-300 bg-gray-100 text-xl font-bold text-gray-700 active:bg-gray-300"
              >
                +
              </button>
            </div>
            {orderUnit > 1 && (
              <span className="absolute right-2 text-xs text-gray-400">
                ({orderUnit}개 단위)
              </span>
            )}
          </div>

          {/* 하단: 총금액 + 버튼들 */}
          <div className="flex items-center gap-2">
            {/* 총 금액 */}
            <div className="flex-1">
              <span className="text-xs text-gray-500">총 금액</span>
              <p className="text-base font-bold text-red-500">
                {formatPrice(totalPrice)}원
              </p>
            </div>

            {/* 버튼들 */}
            <button
              onClick={handleAddToCart}
              className="rounded-lg border-2 border-gray-800 px-5 py-2.5 text-base font-bold text-gray-800 active:bg-gray-100"
            >
              장바구니
            </button>
            <button
              onClick={handleBuyNow}
              className="rounded-lg bg-red-500 px-5 py-2.5 text-base font-bold text-white active:bg-red-600"
            >
              바로구매
            </button>
          </div>
        </div>
      </div>
      {/* 배송권역 안내 시트 (권역 배송비 브랜드일 때만) */}
      {zoneBrandId && (
        <GiftDeliveryZoneGuideModal
          openId={ZONE_GUIDE_MODAL_ID}
          giftItemBrand={zoneBrandId}
        />
      )}
    </div>
  );
};

export default GiftItemDetailPage;
